import { ensureGtagStub, loadGtagScript } from '~/utils/googleAnalytics'
import type { GtmPurchasePayload } from '~/utils/gtmEcommerce'

export interface GoogleAdsConfig {
  enabled: boolean
  conversionId: string
  purchaseConversionLabel: string
}

const CONVERSION_ID_PATTERN = /AW-\d+/
const CONVERSION_LABEL_PATTERN = /^[A-Za-z0-9_-]+$/

export function normalizeGoogleAdsConversionId(value?: string | null): string {
  const match = String(value ?? '').toUpperCase().match(CONVERSION_ID_PATTERN)
  return match ? match[0] : ''
}

/** Accepts the bare label or a full send_to value ("AW-123/abcDEF"). */
export function normalizeGoogleAdsConversionLabel(value?: string | null): string {
  const raw = String(value ?? '').trim()
  const label = raw.includes('/') ? raw.slice(raw.lastIndexOf('/') + 1).trim() : raw
  return CONVERSION_LABEL_PATTERN.test(label) ? label : ''
}

export function parseGoogleAdsConfig(raw?: string | null): GoogleAdsConfig {
  const empty: GoogleAdsConfig = { enabled: false, conversionId: '', purchaseConversionLabel: '' }
  if (!raw?.trim()) return empty

  try {
    const parsed = JSON.parse(raw) as Partial<GoogleAdsConfig>
    const conversionId = normalizeGoogleAdsConversionId(parsed.conversionId)
    return {
      enabled: parsed.enabled === true && Boolean(conversionId),
      conversionId,
      purchaseConversionLabel: normalizeGoogleAdsConversionLabel(parsed.purchaseConversionLabel),
    }
  } catch {
    return empty
  }
}

let initialized = false
let blocked = true
let activeConversionId = ''

function updateAdsConsent(granted: boolean): void {
  if (typeof window.gtag !== 'function') return
  const state = granted ? 'granted' : 'denied'
  window.gtag('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  })
}

export function setGoogleAdsBlocked(value: boolean): void {
  blocked = value
  updateAdsConsent(!blocked)
}

export function isGoogleAdsInitialized(): boolean {
  return initialized
}

export async function initGoogleAds(conversionId: string): Promise<boolean> {
  if (!import.meta.client || blocked) return false

  const id = normalizeGoogleAdsConversionId(conversionId)
  if (!id) return false

  // Commands are queued in dataLayer before gtag.js loads (like Google's own
  // snippet), so a conversion fired right after init is not lost.
  ensureGtagStub()
  updateAdsConsent(true)
  window.gtag?.('js', new Date())
  window.gtag?.('config', id, { send_page_view: false })

  activeConversionId = id
  initialized = true

  await loadGtagScript(id)
  return true
}

export function disableGoogleAds(): void {
  setGoogleAdsBlocked(true)
}

export function enableGoogleAds(): void {
  setGoogleAdsBlocked(false)
}

export function trackGoogleAdsPageView(path: string): void {
  if (!import.meta.client || blocked || !initialized || typeof window.gtag !== 'function') return
  window.gtag('event', 'page_view', {
    send_to: activeConversionId,
    page_path: path,
  })
}

export function trackGoogleAdsPurchase(
  label: string,
  payload: GtmPurchasePayload,
): boolean {
  if (!import.meta.client || blocked || !initialized || typeof window.gtag !== 'function') return false
  if (!label) return false

  window.gtag('event', 'conversion', {
    send_to: `${activeConversionId}/${label}`,
    value: payload.value,
    currency: 'RON',
    transaction_id: payload.transaction_id,
  })
  return true
}
